/**
 * Shared coach-related helper functions
 * Eliminates duplicate getInitials, getDisplayName, getAvatarColor functions
 */

import type { Coach } from '../types';

/**
 * Get initials from a coach's name or email
 * @example getCoachInitials({ first_name: 'John', last_name: 'Doe' }) // "JD"
 */
export function getCoachInitials(coach: Pick<Coach, 'first_name' | 'last_name' | 'email'>): string {
  const first = coach.first_name?.charAt(0) || '';
  const last = coach.last_name?.charAt(0) || '';
  return (first + last).toUpperCase() || coach.email.charAt(0).toUpperCase();
}

/**
 * Get display name from a coach (full name or email fallback)
 * @example getCoachDisplayName({ first_name: 'John', last_name: 'Doe' }) // "John Doe"
 */
export function getCoachDisplayName(coach: Pick<Coach, 'first_name' | 'last_name' | 'email'>): string {
  if (coach.first_name || coach.last_name) {
    return `${coach.first_name || ''} ${coach.last_name || ''}`.trim();
  }
  return coach.email;
}

/**
 * Get a consistent avatar color based on coach name
 * Uses hash of name to ensure same person always gets same color
 */
export function getCoachAvatarColor(coach: Pick<Coach, 'first_name' | 'last_name' | 'email'>): string {
  const colors = [
    'red',
    'pink',
    'grape',
    'violet',
    'indigo',
    'blue',
    'cyan',
    'teal',
    'green',
    'lime',
    'yellow',
    'orange',
  ];
  const name = getCoachDisplayName(coach);
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}
